// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axios from '../utils/axiosConfig';

const RDPViewer = ({ deviceId, hostname, open, onClose }) => {
  const [connectionUrl, setConnectionUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    domain: '',
  });
  const [showCredentials, setShowCredentials] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (open) {
      checkExistingConnection();
    }
  }, [open, deviceId]);

  const checkExistingConnection = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.get(`/api/remote/devices/${deviceId}/rdp-status`);

      if (response.data.rdp_enabled) {
        setConnectionUrl(response.data.connection_url);
        setConnectionId(response.data.connection_id);
        setShowCredentials(false);
      } else {
        setShowCredentials(true);
      }
    } catch (err) {
      console.error('Failed to check RDP status:', err);
      setShowCredentials(true);
    } finally {
      setLoading(false);
    }
  };

  const createConnection = async () => {
    try {
      setLoading(true);
      setError('');

      // Test connection first
      const testResponse = await axios.post('/api/remote/test-connection', {
        device_id: deviceId,
        hostname: hostname,
        port: 3389,
      });

      if (!testResponse.data.reachable) {
        setError(`Cannot reach ${hostname}: ${testResponse.data.message}`);
        setLoading(false);
        return;
      }

      // Enable RDP for the device
      const response = await axios.post(`/api/remote/devices/${deviceId}/enable-rdp`, {
        hostname: hostname,
        username: credentials.username,
        password: credentials.password,
        domain: credentials.domain,
        port: 3389,
      });

      if (response.data.success) {
        setConnectionUrl(response.data.connection_url);
        setConnectionId(response.data.connection_id);
        setShowCredentials(false);
      } else {
        setError('Failed to create RDP connection');
      }
    } catch (err) {
      console.error('Failed to create RDP connection:', err);
      setError(err.response?.data?.detail || 'Error creating RDP connection');
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialSubmit = (e) => {
    e.preventDefault();
    createConnection();
  };

  const handleClose = async () => {
    if (connectionId) {
      try {
        await axios.delete(`/api/remote/devices/${deviceId}/disable-rdp`);
      } catch (err) {
        console.warn('Failed to cleanup connection:', err);
      }
    }
    onClose();
  };

  const handleOpenInNewWindow = () => {
    window.open(connectionUrl, '_blank');
  };

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">RDP Connection to {hostname}</Typography>
          <Box>
            {connectionUrl && (
              <IconButton onClick={handleOpenInNewWindow} size="small" sx={{ mr: 1 }}>
                <OpenInNewIcon />
              </IconButton>
            )}
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          </Box>
        )}

        {showCredentials && !loading && (
          <Box sx={{ p: 3, maxWidth: 400, mx: 'auto', mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Enter RDP Credentials
            </Typography>
            <form onSubmit={handleCredentialSubmit}>
              <TextField
                fullWidth
                label="Username"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                margin="normal"
                required
                autoFocus
              />
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel htmlFor="rdp-password">Password *</InputLabel>
                <Input
                  id="rdp-password"
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={handlePasswordVisibility} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
              <TextField
                fullWidth
                label="Domain (optional)"
                value={credentials.domain}
                onChange={(e) => setCredentials({ ...credentials, domain: e.target.value })}
                margin="normal"
                helperText="Leave empty for local accounts"
              />
              <Box
                sx={{
                  mt: 3,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                }}
              >
                <Button onClick={handleClose} color="inherit">
                  Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={loading}>
                  {loading ? <CircularProgress size={20} /> : 'Connect'}
                </Button>
              </Box>
            </form>
          </Box>
        )}

        {connectionUrl && !loading && !showCredentials && (
          <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
            <iframe
              src={connectionUrl}
              width="100%"
              height="100%"
              frameBorder="0"
              title={`RDP ${deviceId}`}
              style={{
                display: 'block',
                border: 'none',
              }}
              onLoad={() => console.log('RDP session loaded')}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RDPViewer;
